// Cliente HTTP do módulo fiscal.
// A URL do backend vem de REACT_APP_BACKEND_URL (padrão do Emergent).
// O token é o mesmo JWT que o seu sistema já usa: configure como lê-lo com
// configurarFiscal({ getToken: () => ... }) no index.js do app.

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}${process.env.REACT_APP_FISCAL_PREFIX || "/api/fiscal"}`;

let getToken = () => localStorage.getItem("token");
let rotaCertificado = null;

// getToken: como ler o JWT do seu app.
// caminhoCertificado: rota da tela onde o certificado é gerenciado (ex.: "/app/sistema/empresa").
//   Se informado, Fiscal → Configurações mostra só o resumo com um atalho para lá.
// caminhoEmpresa: rota de "Minha empresa" (link "Editar em Minha empresa"); padrão = caminhoCertificado.
let rotaEmpresa = null;
export function configurarFiscal({ getToken: fn, caminhoCertificado: rota, caminhoEmpresa: rotaEmp } = {}) {
  if (fn) getToken = fn;
  if (rota !== undefined) rotaCertificado = rota;
  if (rotaEmp !== undefined) rotaEmpresa = rotaEmp;
}

export const caminhoCertificado = () => rotaCertificado;
// Mesmo login para outros módulos (ex.: Folha de Pagamento)
export const tokenAtual = () => getToken();
export const caminhoEmpresa = () => rotaEmpresa || rotaCertificado;

export class ApiError extends Error {
  constructor(status, detail) {
    super(typeof detail === "string" ? detail : formatarDetalhe(detail));
    this.status = status;
    this.detail = detail;
  }
}

function formatarDetalhe(detail) {
  // Erros de validação do FastAPI: [{loc: [...], msg: "..."}]
  if (Array.isArray(detail)) {
    return detail.map((d) => `${(d.loc || []).slice(1).join(".")}: ${d.msg}`).join("\n");
  }
  return "Erro inesperado";
}

function qs(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
    else sp.append(k, v);
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function request(method, path, { body, params, form, blob } = {}) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload;
  if (form) payload = form;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const resp = await fetch(`${BASE}${path}${qs(params)}`, { method, headers, body: payload });
  if (!resp.ok) {
    let detail = `Erro ${resp.status}`;
    try {
      detail = (await resp.json()).detail ?? detail;
    } catch (_) {
      // corpo não-JSON: veio da hospedagem/proxy, não do módulo fiscal
      if (resp.status >= 502 || resp.status === 500) {
        detail = `O servidor não respondeu (erro ${resp.status} da hospedagem). Aguarde um minuto e tente de novo. `
          + "Se estava emitindo uma nota, confira antes em Notas se ela já foi registrada.";
      }
    }
    throw new ApiError(resp.status, detail);
  }
  if (blob) return resp.blob();
  if (resp.status === 204) return null;
  return resp.json();
}

export const api = {
  get: (p, params) => request("GET", p, { params }),
  post: (p, body, params) => request("POST", p, { body, params }),
  put: (p, body, params) => request("PUT", p, { body, params }),
  del: (p) => request("DELETE", p),
  upload: (p, form) => request("POST", p, { form }),
  blob: (p, params) => request("GET", p, { params, blob: true }),
};

// Abre PDF em nova aba ou baixa arquivo, usando o token (links diretos não levam o header)
// URL local (blob:) de um arquivo da API — para mostrar o PDF dentro da tela, sem janela pop-up
// (o navegador bloqueia janelas abertas depois de esperar a SEFAZ). Revogue com URL.revokeObjectURL.
export async function arquivoUrl(path, params) {
  return URL.createObjectURL(await api.blob(path, params));
}

export async function abrirArquivo(path, { params, nome, baixar = false } = {}) {
  const b = await api.blob(path, params);
  const url = URL.createObjectURL(b);
  if (baixar) {
    const a = document.createElement("a");
    a.href = url;
    a.download = nome || "arquivo";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, "_blank", "noopener");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---- Endpoints nomeados (um lugar só para manter)
export const fiscal = {
  documentos: () => api.get("/documentos"),
  producao: () => api.get("/producao"),
  emissor: () => api.get("/emissor"),
  salvarEmissor: (d) => api.put("/emissor", d),
  sincronizar: () => api.post("/emissor/sincronizar"),
  ajustarNumeracao: (p) => api.put("/emissor/numeracao", undefined, p),

  enviarCertificado: (arquivo, senha, termoVersao) => {
    const f = new FormData();
    f.append("arquivo", arquivo);
    f.append("senha", senha);
    f.append("aceite_termo", "true");
    f.append("termo_versao", termoVersao);
    return api.upload("/certificado", f);
  },
  termoCertificado: () => api.get("/certificado/termo"),
  aceitarTermo: (termoVersao) => {
    const f = new FormData();
    f.append("termo_versao", termoVersao);
    return api.upload("/certificado/aceitar-termo", f);
  },
  historicoTermos: () => api.get("/certificado/termos"),
  removerCertificado: () => api.del("/certificado"),
  statusCertificado: () => api.get("/certificado/status"),
  adminCertificados: () => api.get("/admin/certificados"),
  adminProvedor: () => api.get("/admin/provedor"),
  adminTestarProvedor: (ambiente) => api.post("/admin/provedor/testar", undefined, { ambiente }),
  adminEmpresas: () => api.get("/admin/empresas"),
  adminSincronizarEmpresa: (tenantId) => api.post(`/admin/empresas/${encodeURIComponent(tenantId)}/sincronizar`),
  adminProducaoEmpresa: (tenantId, modo, motivo = "") => api.put(`/admin/empresas/${encodeURIComponent(tenantId)}/producao`, { modo, motivo }),

  buscarDestinatario: (doc) => api.get(`/destinatarios/buscar/${doc}`),
  consultaCnpj: (cnpj) => api.get(`/consulta-cnpj/${cnpj}`),
  consultarEmpresa: (cnpj) => api.get(`/empresa/consulta-cnpj/${cnpj}`),
  destinatarios: (busca) => api.get("/destinatarios", { busca }),
  produtos: (busca) => api.get("/produtos", { busca }),
  criarProduto: (d) => api.post("/produtos", d),
  sugerirTributos: (d) => api.post("/tributos/sugerir", d),
  regrasNcm: () => api.get("/regras-ncm"),
  salvarRegraNcm: (d) => api.put("/regras-ncm", d),
  removerRegraNcm: (p, uf = "*") => api.del(`/regras-ncm/${p}?uf_destino=${encodeURIComponent(uf)}`),
  calcularNfe: (d) => api.post("/nfe/calcular", d),

  emitirNfce: (d) => api.post("/nfce", d),
  buscarNcm: (busca) => api.get("/tabelas/ncm", { busca }),
  ncm: (codigo) => api.get(`/tabelas/ncm/${encodeURIComponent(codigo)}`),
  cest: ({ ncm = "", busca = "" } = {}) => api.get("/tabelas/cest", { ncm, busca }),
  adminTabelas: () => api.get("/admin/tabelas"),
  adminAtualizarTabela: (tipo) => api.post(`/admin/tabelas/${tipo}/atualizar`),
  adminImportarCest: (arquivo) => {
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    return api.upload("/admin/tabelas/cest/importar", fd);
  },
  nfceDaVenda: (vendaId) => api.get(`/nfce/venda/${encodeURIComponent(vendaId)}`),
  emitirNfe: (d) => api.post("/nfe", d),
  reenviar: (id, modelo, d) => api.post(`/${{ "55": "nfe", "65": "nfce", nfse: "nfse" }[modelo]}/${id}/reenviar`, d),

  emitirNfse: (d) => api.post("/nfse", d),
  calcularNfse: (d) => api.post("/nfse/calcular", d),
  nfseParaCorrecao: (id) => api.get(`/nfse/${id}/edicao`),
  municipioNfse: (ibge) => api.get(`/nfse/municipio/${ibge}`),
  servicos: (busca) => api.get("/servicos", { busca }),
  criarServico: (d) => api.post("/servicos", d),
  atualizarServico: (id, d) => api.put(`/servicos/${id}`, d),
  removerServico: (id) => api.del(`/servicos/${id}`),
  sugerirIss: (d) => api.post("/nfse/sugerir-iss", d),
  substituirNfse: (id, d) => api.post(`/nfse/${id}/substituir`, d),
  issMunicipios: () => api.get("/iss-municipios"),
  salvarIssMunicipio: (d) => api.put(`/iss-municipios/${d.codigo_municipio}`, d),
  removerIssMunicipio: (cod) => api.del(`/iss-municipios/${cod}`),
  recorrentes: () => api.get("/nfse/recorrentes"),
  criarRecorrente: (d) => api.post("/nfse/recorrentes", d),
  atualizarRecorrente: (id, d) => api.put(`/nfse/recorrentes/${id}`, d),
  removerRecorrente: (id) => api.del(`/nfse/recorrentes/${id}`),
  emitirRecorrenteAgora: (id) => api.post(`/nfse/recorrentes/${id}/emitir-agora`),
  notas: (params) => api.get("/notas", params),
  nota: (id) => api.get(`/notas/${id}`),
  atualizar: (id) => api.post(`/notas/${id}/atualizar`),
  cancelar: (id, justificativa, codigo_motivo) => api.post(`/notas/${id}/cancelar`, { justificativa, codigo_motivo }),
  cartaCorrecao: (id, correcao) => api.post(`/nfe/${id}/carta-correcao`, { correcao }),
  cartas: (id) => api.get(`/nfe/${id}/carta-correcao`),
  historicoCliente: (doc) => api.get(`/clientes/${doc}/notas`),

  statusSefaz: (modelo) => api.get("/sefaz/status", { modelo }),
  buscarProtocolo: (id) => api.post(`/sefaz/protocolo/${id}`),
  inutilizar: (d) => api.post("/sefaz/inutilizacoes", d),
  inutilizacoes: () => api.get("/sefaz/inutilizacoes"),
  contribuinte: (documento, uf) => api.get("/sefaz/contribuinte", { documento, uf }),

  relatorio: (nome, params) => api.get(`/relatorios/${nome}`, params),
};
